import { useTheme } from "@mui/material";
import {
  APP_ACP,
  CAPACITY_MAX_DAYS,
  QUERY_PROGRESS_SUCCEED,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_TRANSPORT,
  TRACK_EVENTS,
} from "@recare/core/consts";
import { addDaysUnixUtc, getWrittenDate } from "@recare/core/model/utils/dates";
import {
  AuctionRequest,
  Capacity,
  Careprovider,
  SearchType,
} from "@recare/core/types";
import {
  SimpleFormRenderProps,
  convertModelDefinition,
  valueDef,
} from "@recare/react-forms-state";
import { useLocale, useTranslations } from "@recare/translations";
import Translations from "@recare/translations/types";
import {
  useUpdateCapacity,
  useUpdateCareprovider,
} from "apollo/hooks/mutations";
import { useInvalidateCache } from "apollo/utils";
import { useEnvContext } from "context/EnvContext";
import {
  fromUnixTime,
  getHours,
  getMinutes,
  getUnixTime,
  isBefore,
  isToday,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { InfoBanner } from "ds/components/InfoBanner";
import RSButton from "ds/components/RSButton";
import { dp, margin, responsiveCSS } from "ds/materials/metrics";
import { Body, FONT_WEIGHT_BOLD } from "ds/materials/typography";
import { useOnEnter } from "dsl/atoms/Routes";
import { useNotification } from "dsl/organisms/NotificationProvider";
import {
  isOlderThanTwoWeeksAgo,
  useShowNewCapacity,
} from "dsl/organisms/ReceiverDashboard/CapacityNotification/utils";
import { useTracking } from "react-tracking";
import styled from "styled-components";
import { SettingsPageTitle } from "../CareproviderSettings/shared";
import { CapacitySelector } from "./CapacitySelector";

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin: ${margin(3, 0)};
`;

const CapacityPageWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  max-width: ${dp(776)};

  ${responsiveCSS({
    tablet: ["width: 100%"],
  })}
`;

export function shouldHandleTime({
  searchType,
}: {
  searchType: SearchType | undefined;
}): boolean {
  if (
    searchType === SEARCH_TYPE_HOSPITAL ||
    searchType === SEARCH_TYPE_TRANSPORT
  ) {
    return true;
  }
  return false;
}

export function appendTimeToCapacityDate({
  capacityDate,
  capacityTime,
}: {
  capacityDate: number;
  capacityTime: Date;
}): number {
  const start = startOfDay(fromUnixTime(capacityDate));
  return getUnixTime(
    setHours(
      setMinutes(start, getMinutes(capacityTime)),
      getHours(capacityTime),
    ),
  );
}

export function setTimeToMidnight(capacityDate: number) {
  return getUnixTime(
    zonedTimeToUtc(startOfDay(fromUnixTime(capacityDate)), "UTC"),
  );
}
const modelDefinition = convertModelDefinition({
  profile: {
    capacity: {
      ...valueDef("has_capacity"),
      ...valueDef("available_date", {
        convertOut: (
          value: number,
          props: { formInputValue: Careprovider },
          modelValues: Map<string, boolean | Date>,
        ) => {
          const has_capacity = modelValues.get("has_capacity") as boolean;
          const capacityTime = modelValues.get("_capacity_time") as Date;
          const searchType = props?.formInputValue.patient_type;

          // if has_capacity is set to true, remove the available_date
          if (has_capacity) return null;

          if (capacityTime && shouldHandleTime({ searchType })) {
            return appendTimeToCapacityDate({
              capacityDate: value,
              capacityTime,
            });
          }
          // if no capacity_time set and has_capacity is false, we set the available_date to midnight
          return setTimeToMidnight(value);
        },
      }),
      ...valueDef("_capacity_time", {
        convertIn: (_, props: { formInputValue: Careprovider }) => {
          const capacityDate =
            props?.formInputValue?.profile?.capacity?.available_date;
          if (capacityDate) {
            return fromUnixTime(capacityDate);
          }
          return null;
        },
      }),
    },
  },
});

function validateDateAndTime({
  _capacity_time,
  activateNewCapacity,
  dateKey,
  dateToValidate,
  required,
  translations,
}: {
  _capacity_time: number | Date | undefined;
  activateNewCapacity: boolean;
  dateKey: string;
  dateToValidate: number | undefined;
  required?: boolean;
  translations: Translations;
}) {
  const timestampYesterday = addDaysUnixUtc(-1, { endOfDay: true });
  const timestampCapacityMax = addDaysUnixUtc(CAPACITY_MAX_DAYS, {
    endOfDay: true,
  });

  const errorMessageDate = activateNewCapacity
    ? translations.careproviderApp.settings.capacity.tileNoCapacity
        .errorDateInPast
    : translations.general.required;

  const errorMessageTime = activateNewCapacity
    ? translations.careproviderApp.settings.capacity.tileNoCapacity
        .errorTimeInPast
    : translations.general.required;

  const errorMessageTimeInPastDateValid = activateNewCapacity
    ? translations.careproviderApp.settings.capacity.tileNoCapacity
        .errorTimeInPastDateOkay
    : translations.general.required;

  if (required && !dateToValidate && !_capacity_time) {
    return {
      [dateKey]: {
        customMessage: errorMessageDate,
      },
      _capacity_time: {
        customMessage: errorMessageTime,
      },
    };
  }

  if (dateToValidate && !_capacity_time) {
    if (isToday(new Date(dateToValidate * 1000))) {
      return {
        [dateKey]: {
          customMessage: errorMessageTimeInPastDateValid,
        },
        _capacity_time: {
          customMessage: errorMessageTime,
        },
      };
    }

    if (
      (activateNewCapacity && dateToValidate > timestampCapacityMax) ||
      dateToValidate < timestampYesterday
    ) {
      return {
        [dateKey]: {
          customMessage: errorMessageDate,
        },
        _capacity_time: {
          customMessage: errorMessageTime,
        },
      };
    }

    return {
      _capacity_time: {
        customMessage: errorMessageTime,
      },
    };
  }

  if (!dateToValidate && _capacity_time) {
    return {
      [dateKey]: {
        customMessage: errorMessageDate,
      },
    };
  }

  if (dateToValidate && _capacity_time) {
    if (
      isToday(new Date(dateToValidate * 1000)) &&
      isBefore(new Date(_capacity_time), new Date())
    ) {
      return {
        [dateKey]: {
          customMessage: errorMessageTimeInPastDateValid,
        },
        _capacity_time: {
          customMessage: errorMessageTime,
        },
      };
    }

    // for time inputs it's possible to select todays date
    if (
      (activateNewCapacity && dateToValidate > timestampCapacityMax) ||
      dateToValidate < timestampYesterday
    ) {
      return {
        [dateKey]: {
          customMessage: errorMessageDate,
        },
      };
    }
  }

  return true;
}

function validateSingleDate({
  activateNewCapacity,
  dateKey,
  dateToValidate,
  required,
  translations,
}: {
  activateNewCapacity: boolean;
  dateKey: string;
  dateToValidate: number | undefined;
  required?: boolean;
  translations: Translations;
}) {
  const timestampCurrent = addDaysUnixUtc(0, { endOfDay: true });
  const timestampCapacityMax = addDaysUnixUtc(CAPACITY_MAX_DAYS, {
    endOfDay: true,
  });
  const errorMessageDate = activateNewCapacity
    ? translations.careproviderApp.settings.capacity.tileNoCapacity
        .errorDateInPast
    : translations.general.required;

  if (dateToValidate) {
    if (
      (activateNewCapacity && dateToValidate > timestampCapacityMax) ||
      dateToValidate < timestampCurrent
    ) {
      return {
        [dateKey]: {
          customMessage: errorMessageDate,
        },
      };
    }
  }

  if (required && !dateToValidate) {
    return {
      [dateKey]: {
        customMessage: errorMessageDate,
      },
    };
  }

  return true;
}

export function validateCapacity({
  activateNewCapacity,
  dateKey,
  props,
  required,
  searchType,
  value,
}: {
  activateNewCapacity: boolean;
  dateKey: string;
  props: {
    auctionRequest?: AuctionRequest;
    formInputValue: Careprovider;
    translations: Translations;
  };
  required?: boolean;
  searchType: SearchType;
  value: {
    _capacity_time?: number | Date;
    available_date?: number;
    capacity_date?: number;
    has_capacity?: boolean;
  };
}) {
  const { translations } = props;
  const { _capacity_time, available_date, capacity_date, has_capacity } = value;
  const dateToValidate = capacity_date || available_date;
  const handleTime = shouldHandleTime({ searchType });

  if (has_capacity) return true;

  if (handleTime) {
    return validateDateAndTime({
      activateNewCapacity,
      dateKey,
      dateToValidate,
      required,
      translations,
      _capacity_time,
    });
  }
  // Single capacity date validation
  return validateSingleDate({
    activateNewCapacity,
    dateKey,
    dateToValidate,
    required,
    translations,
  });
}

const CapacityTitle = ({
  lastCapacityUpdate,
}: {
  lastCapacityUpdate: Capacity["updated_at"];
}) => {
  const theme = useTheme();
  const { app } = useEnvContext();
  const isAcp = app === APP_ACP;
  const translations = useTranslations();
  const locale = useLocale();

  if (isAcp) return null;

  return (
    <SettingsPageTitle
      title={translations.careproviderApp.settings.capacity.titleNew}
    >
      {lastCapacityUpdate ? (
        <Body
          as="p"
          margin={margin(0)}
          fontWeight={FONT_WEIGHT_BOLD}
          color={theme.palette.grey[600]}
        >
          {translations.careproviderApp.settings.capacity.lastUpdated({
            date: getWrittenDate(lastCapacityUpdate, locale, true),
          })}
        </Body>
      ) : null}
    </SettingsPageTitle>
  );
};

const CapacityNotificationBanner = ({
  careprovider,
}: {
  careprovider: Careprovider;
}) => {
  const translations = useTranslations();
  const notify = useNotification();
  const { confirmCapacity, mutationProgress } = useUpdateCapacity({
    careprovider,
    onError: () =>
      notify({
        message: translations.auctionRequest.tryAgain,
        type: "error",
      }),
  });

  const isOutdated =
    (!careprovider.profile?.capacity?.updated_at ||
    isOlderThanTwoWeeksAgo(careprovider.profile.capacity.updated_at)) && careprovider.profile?.capacity?.has_capacity;

  if (!isOutdated) {
    // if capacity is up to date, show nothing
    if (mutationProgress !== QUERY_PROGRESS_SUCCEED) return null;

    // if capacity is updated and the confirmCapacityy was successfull, show success banner
    return (
      <InfoBanner
        wrapperStyle={{ width: "fit-content", margin: margin(0, 0, 3) }}
        severity="success"
        message={
          translations.careproviderApp.settings.capacity.banner.confirmation
        }
      />
    );
  }

  // if capacity is not up to date, show update banner
  return (
    <InfoBanner
      wrapperStyle={{ margin: margin(0, 0, 3) }}
      severity="warning"
      header={translations.careproviderApp.settings.capacity.banner.title}
      message={translations.careproviderApp.settings.capacity.banner.subtitle}
      actions={[
        {
          label: translations.careproviderApp.settings.capacity.banner.button,
          onClick: () => confirmCapacity(),
        },
      ]}
    />
  );
};

export default function CareproviderSettingsCapacityNew({
  careprovider,
}: {
  careprovider: Careprovider;
}) {
  useOnEnter();
  const activateNewCapacity = useShowNewCapacity();
  const { trackEvent } = useTracking();
  const notify = useNotification();
  const translations = useTranslations();
  const invalidateCache = useInvalidateCache();
  const { app } = useEnvContext();
  const isAcp = app === APP_ACP;

  const [updateCareprovider, queryProgress, resetProgress] =
    useUpdateCareprovider({
      id: careprovider.id,
      onCompleted() {
        setTimeout(() => resetProgress(), 3000);
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_SAVED_CHANGES,
        });
        invalidateCache([`QUERY.careproviderRequestsNew`]);
        if (!careprovider.profile?.capacity?.has_capacity) {
          notify({
            type: "success",
            message:
              translations.careproviderApp.settings.capacity
                .toastMessageHasNoCapacitySaved,
          });
        } else {
          notify({
            type: "success",
            message:
              translations.careproviderApp.settings.capacity
                .toastMessageHasCapacitySaved,
          });
        }
      },
      onError(err) {
        console.error(JSON.stringify(err));
        notify({ type: "error", message: translations.general.error });
      },
    });

  return (
    <SimpleFormRenderProps
      asHtmlForm
      formInputValue={careprovider}
      formStyle={{ alignItems: "flex-start" }}
      modelDefinition={modelDefinition}
      onSubmit={(value) => updateCareprovider(value)}
      validate={(value, props) =>
        validateCapacity({
          activateNewCapacity,
          value,
          props,
          required: true,
          dateKey: "available_date",
          searchType: props.formInputValue?.patient_type,
        })
      }
    >
      {({ submit }) => {
        return (
          <CapacityPageWrapper>
            <CapacityTitle
              lastCapacityUpdate={careprovider.profile?.capacity?.updated_at}
            />
            {!isAcp && (
              <>
                <CapacityNotificationBanner careprovider={careprovider} />
                <Body as="p" margin={margin(0, 0, 2)}>
                  {translations.careproviderApp.settings.capacity.explanation}
                </Body>
              </>
            )}
            <CapacitySelector careprovider={careprovider} />
            <ButtonWrapper data-testid="save-capacity-wrapper">
              <RSButton
                style={{ width: "100%", margin: margin(0) }}
                color="primary"
                id="submit-capacity"
                loading={queryProgress}
                type="submit"
                onClick={submit}
                variant="contained"
              >
                {translations.careproviderApp.settings.capacity.saveButton}
              </RSButton>
            </ButtonWrapper>
          </CapacityPageWrapper>
        );
      }}
    </SimpleFormRenderProps>
  );
}
